<script setup>
import { ref, computed } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { required, email } from '@vuelidate/validators';
import WootSubmitButton from 'dashboard/components/buttons/FormSubmitButton.vue';
import FormLabel from 'dashboard/components/widgets/forms/FormLabel.vue';

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();

const agentName = ref('');
const agentEmail = ref('');
const selectedRoleId = ref('agent');

const rules = {
  agentName: { required },
  agentEmail: { required, email },
  selectedRoleId: { required },
};

const v$ = useVuelidate(rules, {
  agentName,
  agentEmail,
  selectedRoleId,
});

const uiFlags = useMapGetter('agents/getUIFlags');
const getCustomRoles = useMapGetter('customRole/getCustomRoles');

const roles = computed(() => {
  const defaultRoles = [
    {
      id: 'administrator',
      name: 'administrator',
      label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
    },
    {
      id: 'agent',
      name: 'agent',
      label: t('AGENT_MGMT.AGENT_TYPES.AGENT'),
    },
  ];

  const customRoles = getCustomRoles.value.map(role => ({
    id: role.id,
    name: `custom_${role.id}`,
    label: role.name,
  }));

  return [...defaultRoles, ...customRoles];
});

const selectedRole = computed(() =>
  roles.value.find(
    role =>
      role.id === selectedRoleId.value || role.name === selectedRoleId.value
  )
);

const addAgent = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    const payload = {
      name: agentName.value,
      email: agentEmail.value,
    };

    if (selectedRole.value.name.startsWith('custom_')) {
      payload.custom_role_id = selectedRole.value.id;
    } else {
      payload.role = selectedRole.value.name;
    }

    await store.dispatch('agents/create', payload);
    useAlert(t('AGENT_MGMT.ADD.API.SUCCESS_MESSAGE'), {type: 'success'});
    emit('close');
  } catch (error) {
    const {
      response: {
        data: {
          error: errorResponse = '',
          attributes: attributes = [],
          message: attrError = '',
        } = {},
      } = {},
    } = error;

    let errorMessage = '';
    if (error?.response?.status === 422 && !attributes.includes('base')) {
      errorMessage = t('AGENT_MGMT.ADD.API.EXIST_MESSAGE');
    } else {
      errorMessage = t('AGENT_MGMT.ADD.API.ERROR_MESSAGE');
    }
    useAlert(errorResponse || attrError || errorMessage, {type: 'error'});
  }
};

</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="$t('AGENT_MGMT.ADD.TITLE')"
    />
    <form class="flex flex-col items-start w-full gap-6" @submit.prevent="addAgent" @keydown.enter.prevent>
      <woot-input
        v-model="agentName"
        :class="{ error: v$.agentName.$error }"
        class="w-full input-form"
        :label="$t('AGENT_MGMT.ADD.FORM.NAME.LABEL')"
        :placeholder="$t('AGENT_MGMT.ADD.FORM.NAME.PLACEHOLDER')"
        :error="v$.agentName.$error ? $t('AGENT_MGMT.ADD.FORM.NAME.ERROR') : ''"
        :required="true"
        type="text"
        @blur="v$.agentName.$touch"
        @input="v$.agentName.$touch"
      />

      <div class="w-full">
        <label :class="{ error: v$.selectedRoleId.$error }">
          <FormLabel
            :label="$t('AGENT_MGMT.ADD.FORM.AGENT_TYPE.LABEL')"
            :required="true"
            :has-error="v$.selectedRoleId.$error"
          />
          <select v-model="selectedRoleId" class="input-form" @change="v$.selectedRoleId.$touch">
            <option v-for="role in roles" :key="role.id" :value="role.id">
              {{ role.label }}
            </option>
          </select>
          <span v-if="v$.selectedRoleId.$error" class="message">
            {{ $t('AGENT_MGMT.ADD.FORM.AGENT_TYPE.ERROR') }}
          </span>
        </label>
      </div>

      <woot-input
        v-model="agentEmail"
        :class="{ error: v$.agentEmail.$error }"
        class="w-full input-form"
        :label="$t('AGENT_MGMT.ADD.FORM.EMAIL.LABEL')"
        :placeholder="$t('AGENT_MGMT.ADD.FORM.EMAIL.PLACEHOLDER')"
        :error="v$.agentEmail.$error ? $t('AGENT_MGMT.ADD.FORM.EMAIL.ERROR') : ''"
        :required="true"
        type="email"
        @blur="v$.agentEmail.$touch"
        @input="v$.agentEmail.$touch"
      />

      <div class="flex flex-row justify-center w-full gap-3 px-0 py-2">
          <button class="input-base bg-slate-100 dark:bg-slate-700 !text-slate-700 dark:!text-slate-300 rounded-lg" @click.prevent="emit('close')">
            {{ $t('AGENT_MGMT.ADD.CANCEL_BUTTON_TEXT') }}
          </button>
          <WootSubmitButton
            class="!bg-grass-500 !text-white rounded-lg"
            :disabled="v$.$invalid || uiFlags.isCreating"
            :button-text="$t('AGENT_MGMT.ADD.FORM.SUBMIT')"
            :loading="uiFlags.isCreating"
          />      
      </div>
    </form>
  </div>
</template>
